/**
 * El motor de la escena «En órbita» (encargo 117), portado de la maqueta aprobada
 * (docs/design/mejoras/demos/primeras-semanas-4/assets/nave-orbita.js). Es imperativo a propósito:
 * el anillo gira con requestAnimationFrame (se arrastra con el dedo y sigue con inercia), las piezas
 * llegan con WAAPI y el despegue encadena cuenta, fuego y vuelo. React sólo le da el contenedor
 * (ShipScene.tsx) y lo desmonta; el motor se apaga solo cuando su raíz sale del DOM.
 *
 * Movimiento reducido (`reduced()`): el anillo no gira, las piezas aparecen con un fundido y el
 * despegue no tiembla ni vuela (los módulos se funden, se prenden los motores y listo).
 */
import type { SfxName } from '../../../lib/sfx/index.js';
import {
  C,
  R,
  FLAMES,
  SHIP_COLOR,
  SHIP_ICONS,
  moduleSvg,
  sceneSvg,
  slotAngle,
  type ModuleArt,
} from './shipArt.js';

export interface SceneOptions {
  pieces: readonly (ModuleArt & { name: string; feature: string })[];
  placed: ReadonlySet<string>;
  play: (name: SfxName) => void;
  reduced: () => boolean;
  /** Al tocar un módulo: el globo lo explica; el contenedor puede además llevar a la actividad. */
  onPieceTap?: (id: string, placed: boolean) => void;
}

interface Spin {
  th: number;
  vel: number;
  base: number;
  radius: number;
  t: number;
  held: boolean;
}

export interface SceneHandle {
  root: HTMLElement;
  /** Una pieza nueva: entra desde afuera de la pantalla, en espiral, y se acopla en su lugar. */
  win: (id: string) => Promise<void>;
  /** Nave completa: el anillo acelera y los brazos laten. */
  complete: () => void;
  /** El despegue: cuenta 3-2-1, los módulos se funden, fuego, humo y sale por arriba. */
  launch: (layer: HTMLElement) => Promise<void>;
}

const wait = (ms: number) => new Promise<void>((ok) => setTimeout(ok, ms));

function stars(box: HTMLElement, n: number, from = 0): void {
  // Cada estrella va dos veces (mitad de arriba y de abajo): al volar, la capa se desliza una pantalla
  // entera hacia abajo y vuelve a empezar sin que se note el salto.
  for (let i = from; i < from + n; i++) {
    for (const half of [0, 50]) {
      const s = document.createElement('i');
      s.style.left = `${(i * 37) % 100}%`;
      s.style.top = `${half + ((i * 61) % 100) / 2}%`;
      s.style.setProperty('--d', `${(i % 7) * 0.4}s`);
      box.append(s);
    }
  }
}

function buildScene(el: HTMLElement, opts: SceneOptions): HTMLElement {
  el.innerHTML = `<div class="ship-scene"><div class="ship-stars"></div><div class="ship-planet"></div><div class="ship-frame">${sceneSvg(opts.pieces, opts.placed)}</div><div class="ship-bubble" hidden></div></div>`;
  const root = el.querySelector<HTMLElement>('.ship-scene');
  if (!root) throw new Error('ship: no se armó la escena');
  const box = root.querySelector<HTMLElement>('.ship-stars');
  if (box) stars(box, 20);
  return root;
}

function motor(root: HTMLElement, reduced: () => boolean): Spin {
  const ring = root.querySelector('.ship-ring');
  const spin: Spin = {
    th: 0,
    vel: reduced() ? 0 : 9,
    base: reduced() ? 0 : 9,
    radius: R,
    t: performance.now(),
    held: false,
  };
  const step = (now: number) => {
    if (!root.isConnected || !ring) return;
    const dt = Math.min(0.05, (now - spin.t) / 1000);
    spin.t = now;
    if (!spin.held) {
      spin.th += spin.vel * dt;
      spin.vel += (spin.base - spin.vel) * Math.min(1, dt * 1.6);
    }
    ring.setAttribute('transform', `rotate(${spin.th.toFixed(2)} ${C} ${C})`);
    ring.querySelectorAll('.ship-pos').forEach((g) => {
      const i = Number((g.parentNode as Element | null)?.getAttribute('data-i'));
      const a = (slotAngle(i) * Math.PI) / 180;
      const r = spin.radius;
      // Cada módulo se endereza (rota al revés que el anillo): los íconos quedan siempre derechos.
      g.setAttribute(
        'transform',
        `translate(${(C + r * Math.cos(a)).toFixed(1)} ${(C + r * Math.sin(a)).toFixed(1)}) rotate(${(-spin.th).toFixed(2)})`,
      );
    });
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
  return spin;
}

/** De coordenadas del dibujo (300×300) a píxeles dentro de la escena. */
function toPx(root: HTMLElement, x: number, y: number): [number, number, number] {
  const svg = root.querySelector('.ship-svg')?.getBoundingClientRect();
  const r = root.getBoundingClientRect();
  if (!svg) return [0, 0, 1];
  const s = Math.min(svg.width, svg.height) / 300;
  return [
    svg.left - r.left + (svg.width - 300 * s) / 2 + x * s,
    svg.top - r.top + (svg.height - 300 * s) / 2 + y * s,
    s,
  ];
}

function flash(root: HTMLElement, x: number, y: number, cls = 'ship-flash'): void {
  const d = document.createElement('i');
  d.className = cls;
  d.style.left = `${x}px`;
  d.style.top = `${y}px`;
  root.append(d);
  void d
    .animate(
      [
        { transform: 'translate(-50%,-50%) scale(.3)', opacity: 1 },
        { transform: 'translate(-50%,-50%) scale(2.6)', opacity: 0 },
      ],
      { duration: 700, easing: 'ease-out' },
    )
    .finished.then(() => d.remove());
}

function smoke(root: HTMLElement): void {
  for (const [x, y, w] of FLAMES) {
    const [px, py, s] = toPx(root, x, y + w * 2.6);
    for (let k = 0; k < 6; k++) {
      const b = document.createElement('i');
      b.className = 'ship-smoke';
      b.style.left = `${px}px`;
      b.style.top = `${py}px`;
      root.append(b);
      const side = k % 2 ? 1 : -1;
      void b
        .animate(
          [
            { transform: 'translate(-50%,-50%) scale(.7)', opacity: 0.9 },
            {
              transform: `translate(calc(-50% + ${side * (40 + k * 14) * s}px), calc(-50% + ${(6 + k * 3) * s}px)) scale(${2.2 + k * 0.3})`,
              opacity: 0,
            },
          ],
          { duration: 1500 + k * 120, delay: k * 90, easing: 'ease-out' },
        )
        .finished.then(() => b.remove());
    }
  }
}

/** Monta la escena tocable del Inicio. */
export function mountScene(el: HTMLElement, opts: SceneOptions): SceneHandle {
  const root = buildScene(el, opts);
  const spin = motor(root, opts.reduced);
  const bubble = root.querySelector<HTMLElement>('.ship-bubble');
  const placed = new Set(opts.placed);
  let drag: {
    a: number;
    t: number;
    moved: boolean;
    x: number;
    y: number;
    v: number;
    target: Element;
  } | null = null;
  let bubbleTimer = 0;

  const angleOf = (e: PointerEvent) => {
    const [cx, cy] = toPx(root, C, C);
    const r = root.getBoundingClientRect();
    return (Math.atan2(e.clientY - r.top - cy, e.clientX - r.left - cx) * 180) / Math.PI;
  };
  root.addEventListener('pointerdown', (e) => {
    drag = {
      a: angleOf(e),
      t: performance.now(),
      moved: false,
      x: e.clientX,
      y: e.clientY,
      v: 0,
      target: e.target as Element,
    };
    root.setPointerCapture?.(e.pointerId);
  });
  root.addEventListener('pointermove', (e) => {
    if (!drag || opts.reduced()) return;
    if (Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 6) drag.moved = true;
    if (!drag.moved) return;
    spin.held = true;
    const a = angleOf(e);
    let d = a - drag.a;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    const now = performance.now();
    drag.v = (d / Math.max(1, now - drag.t)) * 1000;
    spin.th += d;
    drag.a = a;
    drag.t = now;
  });
  const release = (e: PointerEvent) => {
    const d = drag;
    drag = null;
    spin.held = false;
    if (!d || e.type === 'pointercancel') return;
    if (d.moved) {
      spin.vel = Math.max(-900, Math.min(900, d.v));
      if (Math.abs(spin.vel) > 240) opts.play('ship-spin');
      return;
    }
    const mod = d.target.closest?.('.ship-mod');
    if (mod) {
      const id = mod.getAttribute('data-piece') ?? '';
      const p = opts.pieces.find((x) => x.id === id);
      if (!p) return;
      const isPlaced = mod.classList.contains('is-placed');
      mod.classList.remove('is-tap');
      void mod.getBoundingClientRect();
      mod.classList.add('is-tap');
      showBubble(
        isPlaced
          ? `<b>${p.name}</b>${p.feature}`
          : `<b>Falta: ${p.name}</b>Se gana con: ${p.feature}`,
        e,
      );
      opts.play(isPlaced ? 'ship-piece' : 'ship-tap');
      opts.onPieceTap?.(id, isPlaced);
    } else if (d.target.closest?.('.ship-craft')) {
      if (!opts.reduced()) spin.vel += 420;
      const craft = root.querySelector('.ship-craft');
      craft?.classList.remove('is-nudge');
      void root.getBoundingClientRect();
      craft?.classList.add('is-nudge');
      opts.play('ship-spin');
    }
  };
  root.addEventListener('pointerup', release);
  root.addEventListener('pointercancel', release);

  function showBubble(html: string, e: PointerEvent) {
    if (!bubble) return;
    const r = root.getBoundingClientRect();
    bubble.innerHTML = html;
    bubble.hidden = false;
    bubble.style.left = `${Math.min(Math.max(e.clientX - r.left, 70), r.width - 70)}px`;
    bubble.style.top = `${Math.max(e.clientY - r.top - 14, 30)}px`;
    window.clearTimeout(bubbleTimer);
    bubbleTimer = window.setTimeout(() => (bubble.hidden = true), 1900);
  }

  function place(id: string): Element | null {
    const i = opts.pieces.findIndex((x) => x.id === id);
    const p = opts.pieces[i];
    const g = root.querySelector(`.ship-mod[data-piece="${id}"]`);
    if (!p || !g) return null;
    placed.add(id);
    g.insertAdjacentHTML('afterend', moduleSvg(p, i, true));
    g.remove();
    const arc = 2 * Math.PI * R;
    root
      .querySelector('.ship-prog')
      ?.setAttribute(
        'stroke-dasharray',
        `${((placed.size / 7) * arc).toFixed(1)} ${arc.toFixed(1)}`,
      );
    root
      .querySelector('.ship-svg')
      ?.setAttribute('aria-label', `Tu nave: ${placed.size} de 7 piezas`);
    return root.querySelector(`.ship-mod[data-piece="${id}"]`);
  }

  async function win(id: string): Promise<void> {
    const i = opts.pieces.findIndex((x) => x.id === id);
    const p = opts.pieces[i];
    if (!p || placed.has(id)) return;
    opts.play('ship-win');
    if (opts.reduced()) {
      await wait(400);
      place(id)?.classList.add('is-dock');
      opts.play('ship-dock');
      return;
    }
    const before = spin.base;
    spin.base = 0;
    spin.vel = 0;
    await wait(120);
    const a = ((slotAngle(i) + spin.th) * Math.PI) / 180;
    const [tx, ty, s] = toPx(root, C + R * Math.cos(a), C + R * Math.sin(a));
    const r = root.getBoundingClientRect();
    const fly = document.createElement('div');
    fly.className = 'ship-incoming';
    fly.innerHTML = `<svg viewBox="-24 -24 48 48"><circle r="20" fill="${SHIP_COLOR[p.medium]}"/><circle r="20" fill="url(#ship-shine)"/><g transform="translate(-9 -9) scale(0.75)" fill="none" stroke="#151622" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${SHIP_ICONS[p.medium]}</g></svg>`;
    fly.style.width = fly.style.height = `${48 * s}px`;
    root.append(fly);
    // Una espiral que entra desde afuera (arriba a la derecha), da media vuelta alrededor de la nave
    // y se acopla en su lugar.
    const [cx, cy] = toPx(root, C, C);
    const pts: [number, number, number][] = [];
    const a0 = a + Math.PI * 1.3;
    for (let k = 0; k <= 10; k++) {
      const t = k / 10;
      const ang = a0 + (a - a0) * t;
      const rad = (R * 2.1 - R * 1.1 * t) * s;
      pts.push([cx + rad * Math.cos(ang), cy + rad * Math.sin(ang), 1.6 - 0.6 * t]);
    }
    pts[10] = [tx, ty, 1];
    const at = (x: number, y: number, k: number) => ({
      transform: `translate(${x - 24 * s}px, ${y - 24 * s}px) scale(${k})`,
    });
    await fly.animate(
      [
        { ...at(r.width + 40, -40, 1.8), opacity: 0 },
        ...pts.map(([x, y, k], j) => ({
          ...at(x, y, k),
          opacity: 1,
          offset: 0.15 + (0.85 * j) / 10,
        })),
      ],
      { duration: 1900, easing: 'cubic-bezier(.4,0,.3,1)' },
    ).finished;
    fly.remove();
    place(id)?.classList.add('is-dock');
    opts.play('ship-dock');
    flash(root, tx, ty);
    await wait(500);
    spin.base = before;
  }

  function complete(): void {
    root.classList.add('is-ready');
    if (!opts.reduced()) spin.base = 16;
  }

  async function launch(layer: HTMLElement): Promise<void> {
    const frame = root.querySelector<HTMLElement>('.ship-frame');
    if (!frame) return;
    if (opts.reduced()) {
      root.classList.add('is-fused', 'is-firing');
      opts.play('ship-launch');
      await wait(700);
      layer.classList.add('is-night');
      return;
    }
    const num = document.createElement('div');
    num.className = 'ship-count';
    layer.append(num);
    for (const t of ['3', '2', '1']) {
      num.textContent = t;
      num.animate(
        [
          { transform: 'scale(1.6)', opacity: 0 },
          { transform: 'scale(1)', opacity: 1, offset: 0.3 },
          { transform: 'scale(.9)', opacity: 0 },
        ],
        { duration: 640 },
      );
      opts.play('ship-count');
      spin.base += 70;
      // En el «1» los motores se prenden bajito: se nota que va a salir.
      if (t === '1') root.classList.add('is-spark');
      await wait(650);
    }
    num.remove();
    // Los módulos se cierran girando sobre la nave y se funden con ella.
    opts.play('ship-ignite');
    spin.base = 1100;
    const t0 = performance.now();
    await new Promise<void>((ok) => {
      const close = (now: number) => {
        const t = Math.min(1, (now - t0) / 1200);
        spin.radius = R - (R - 18) * t * t;
        if (t > 0.72) root.classList.add('is-fused');
        if (t < 1) requestAnimationFrame(close);
        else ok();
      };
      requestAnimationFrame(close);
    });
    const [cx, cy] = toPx(root, C, C);
    flash(root, cx, cy, 'ship-flash is-big');
    // Motores a fondo: fuego, humo y temblor; después sale por arriba y las estrellas pasan.
    root.classList.add('is-firing');
    opts.play('ship-launch');
    smoke(root);
    await frame.animate(
      [
        { transform: 'translate(0,0)' },
        { transform: 'translate(-1.5px,1px)' },
        { transform: 'translate(1.5px,-1px)' },
        { transform: 'translate(-1px,-1px)' },
        { transform: 'translate(1px,1px)' },
        { transform: 'translate(0,0)' },
      ],
      { duration: 140, iterations: 5 },
    ).finished;
    const box = root.querySelector<HTMLElement>('.ship-stars');
    if (box) stars(box, 18, 20);
    root.classList.add('is-flying');
    await frame.animate(
      [
        { transform: 'translateY(0) scale(1)' },
        { transform: 'translateY(-8%) scale(1)', offset: 0.35 },
        { transform: 'translateY(-125%) scale(0.86)' },
      ],
      { duration: 1500, easing: 'cubic-bezier(.55,0,.85,.4)', fill: 'forwards' },
    ).finished;
    await wait(250);
    layer.classList.add('is-night');
    await wait(250);
  }

  return { root, win, complete, launch };
}

/** La escena de la presentación: las 7 piezas puestas, latiendo de a una, sin tocar. */
export function mountIntroScene(
  el: HTMLElement,
  opts: Omit<SceneOptions, 'placed' | 'onPieceTap'>,
): void {
  const root = buildScene(el, { ...opts, placed: new Set(opts.pieces.map((p) => p.id)) });
  root.classList.add('is-intro', 'is-ready');
  motor(root, opts.reduced);
}

/** Puebla de estrellas la ventanita de vuelo de «¡Despegaste!» (ver ShipFlight). */
export function seedFlightStars(box: HTMLElement): void {
  stars(box, 30);
}
